import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReleaseState, Cover, message } from '@common/musicHubCommon';
import { ROUTES } from '../../../routes';
import './index.scss';

// 删除确认对话框组件 - 按照设计稿样式
interface DeleteConfirmProps {
  visible: boolean;
  title: string;
  type: 'song' | 'album';
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmDialog: React.FC<DeleteConfirmProps> = ({ visible, title, type, onConfirm, onCancel }) => {
  if (!visible) return null;
  
  return (
    <div className="delete-dialog-overlay">
      {/* Backdrop */}
      <div className="delete-dialog-backdrop" onClick={onCancel} />
      
      {/* Dialog */}
      <div className="delete-dialog">
        {/* Content */}
        <div className="delete-dialog__content">
          <h3 className="delete-dialog__title">Delete "{title}"?</h3>
          <p className="delete-dialog__desc">
            This {type} will be permanently removed and cannot be undone.
          </p>
        </div>
        
        {/* Buttons */}
        <div className="delete-dialog__buttons">
          <button className="delete-dialog__btn delete-dialog__btn--confirm" onClick={onConfirm}>
            Delete
          </button>
          <button className="delete-dialog__btn delete-dialog__btn--cancel" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Mock 数据
const MOCK_ALBUMS = [
  {
    id: '1',
    title: 'Night Owl EP',
    artist: 'Omah Gay',
    cover: undefined,
    tracks: 5,
    genre: ['Pop', 'Electronic'],
    releaseDate: '2026-01-10',
    status: 'published' as const,
  },
  {
    id: '2',
    title: 'Summer Collection',
    artist: 'Omah Gay',
    cover: undefined,
    tracks: 8,
    genre: ['Afrobeats', 'R&B'],
    releaseDate: '2025-08-15',
    status: 'review' as const,
  },
];

// 专辑行组件（桌面端）
interface AlbumRowProps {
  album: typeof MOCK_ALBUMS[0];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const AlbumRow: React.FC<AlbumRowProps> = ({ album, onEdit, onDelete }) => (
  <tr className="album-row">
    <td className="album-row__cell album-row__cell--album">
      <div className="album-cell">
        <Cover src={album.cover} size="small" />
        <span className="album-cell__title">{album.title}</span>
      </div>
    </td>
    <td className="album-row__cell">{album.tracks} {album.tracks === 1 ? 'track' : 'tracks'}</td>
    <td className="album-row__cell">{album.releaseDate}</td>
    <td className="album-row__cell">
      <ReleaseState state={album.status} />
    </td>
    <td className="album-row__cell album-row__cell--actions">
      <button className="action-btn" onClick={() => onEdit(album.id)}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path 
            d="M17.836 3.54295L12.109 9.26995C11.8122 9.56479 11.6102 9.94156 11.529 10.3519L11 12.9999L13.648 12.4699C14.058 12.3879 14.434 12.1869 14.73 11.8909L20.457 6.16395C20.6291 5.99185 20.7656 5.78754 20.8588 5.56269C20.9519 5.33783 20.9998 5.09683 20.9998 4.85345C20.9998 4.61007 20.9519 4.36907 20.8588 4.14421C20.7656 3.91936 20.6291 3.71505 20.457 3.54295C20.2849 3.37085 20.0806 3.23434 19.8557 3.1412C19.6309 3.04806 19.3899 3.00012 19.1465 3.00012C18.9031 3.00012 18.6621 3.04806 18.4373 3.1412C18.2124 3.23434 18.0081 3.37085 17.836 3.54295Z" 
            stroke="white" strokeOpacity="0.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          />
          <path 
            d="M12 4H4.5C4.22386 4 4 4.22386 4 4.5V19.5C4 19.7761 4.22386 20 4.5 20H19.5C19.7761 20 20 19.7761 20 19.5V12" 
            stroke="white" strokeOpacity="0.5" strokeWidth="2"
          />
        </svg>
      </button>
      <button className="action-btn action-btn--danger" onClick={() => onDelete(album.id)}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M10 5V4C10 3.46957 10.2107 2.96086 10.5858 2.58579C10.9609 2.21071 11.4696 2 12 2C12.5304 2 13.0391 2.21071 13.4142 2.58579C13.7893 2.96086 14 3.46957 14 4V5M4 7V19C4 19.5304 4.21071 20.0391 4.58579 20.4142C4.96086 20.7893 5.46957 21 6 21H18C18.5304 21 19.0391 20.7893 19.4142 20.4142C19.7893 20.0391 20 19.5304 20 19V7M4 7H20M4 7H2M20 7H22M9 17V11M15 17V11M7 5H17" stroke="white" strokeOpacity="0.5" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>
    </td>
  </tr>
);

// 移动端专辑卡片组件
interface MobileAlbumCardProps {
  album: typeof MOCK_ALBUMS[0];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const MobileAlbumCard: React.FC<MobileAlbumCardProps> = ({ album, onEdit, onDelete }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);
  
  return (
    <div className="mobile-album-card">
      <Cover src={album.cover} size="medium" />
      <div className="mobile-album-card__info">
        <div className="mobile-album-card__header" ref={menuRef}>
          <span className="mobile-album-card__title">{album.title}</span>
          <button className="mobile-album-card__menu-btn" onClick={() => setShowMenu(!showMenu)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </button>
          {showMenu && (
            <div className="mobile-album-card__dropdown">
              <button onClick={() => { onEdit(album.id); setShowMenu(false); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M17.836 3.54295L12.109 9.26995C11.8122 9.56479 11.6102 9.94156 11.529 10.3519L11 12.9999L13.648 12.4699C14.058 12.3879 14.434 12.1869 14.73 11.8909L20.457 6.16395C20.6291 5.99185 20.7656 5.78754 20.8588 5.56269C20.9519 5.33783 20.9998 5.09683 20.9998 4.85345C20.9998 4.61007 20.9519 4.36907 20.8588 4.14421C20.7656 3.91936 20.6291 3.71505 20.457 3.54295C20.2849 3.37085 20.0806 3.23434 19.8557 3.1412C19.6309 3.04806 19.3899 3.00012 19.1465 3.00012C18.9031 3.00012 18.6621 3.04806 18.4373 3.1412C18.2124 3.23434 18.0081 3.37085 17.836 3.54295Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 4H4.5C4.22386 4 4 4.22386 4 4.5V19.5C4 19.7761 4.22386 20 4.5 20H19.5C19.7761 20 20 19.7761 20 19.5V12" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Edit
              </button>
              <button onClick={() => { onDelete(album.id); setShowMenu(false); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M10 5V4C10 3.46957 10.2107 2.96086 10.5858 2.58579C10.9609 2.21071 11.4696 2 12 2C12.5304 2 13.0391 2.21071 13.4142 2.58579C13.7893 2.96086 14 3.46957 14 4V5M4 7V19C4 19.5304 4.21071 20.0391 4.58579 20.4142C4.96086 20.7893 5.46957 21 6 21H18C18.5304 21 19.0391 20.7893 19.4142 20.4142C19.7893 20.0391 20 19.5304 20 19V7M4 7H20M4 7H2M20 7H22M9 17V11M15 17V11M7 5H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Delete
              </button>
            </div>
          )}
        </div>
        <span className="mobile-album-card__tracks">{album.tracks} {album.tracks === 1 ? 'track' : 'tracks'}</span>
        <div className="mobile-album-card__footer">
          <span className="mobile-album-card__date">Release: {album.releaseDate}</span>
          <ReleaseState state={album.status} />
        </div>
      </div>
    </div>
  );
};

const MyAlbums: React.FC = () => {
  const navigate = useNavigate();
  const [albums, setAlbums] = useState(MOCK_ALBUMS);
  const [deleteModal, setDeleteModal] = useState<{ visible: boolean; id: string; title: string }>({
    visible: false,
    id: '',
    title: '',
  });

  const handleEdit = (id: string) => {
    const album = albums.find(a => a.id === id);
    if (album) {
      // Mock songs for this album (在实际项目中应该从后端获取)
      const mockSongs = [
        {
          id: `${id}-song-1`,
          title: 'Monday',
          artist: album.artist,
          album: album.title,
          cover: album.cover,
          genres: album.genre,
          releaseDate: album.releaseDate,
          status: album.status === 'published' ? 'Released' as const : album.status === 'review' ? 'Under Review' as const : 'Rejected' as const,
        },
      ];
      
      const albumData = {
        id: album.id,
        title: album.title,
        artist: album.artist,
        tracks: album.tracks,
        cover: album.cover,
        genres: album.genre,
        releaseDate: album.releaseDate,
        status: album.status === 'published' ? 'Released' as const : album.status === 'review' ? 'Under Review' as const : 'Rejected' as const,
        songs: mockSongs,
      };
      
      // 跳转到 AlbumInfo 页面（专辑信息概览+歌曲列表）
      navigate(`/album/${id}/info`, {
        state: {
          album: albumData,
          returnTo: ROUTES.myAlbums,
        },
      });
    }
  };

  const handleDelete = (id: string) => {
    const album = albums.find(a => a.id === id);
    if (album) {
      setDeleteModal({ visible: true, id, title: album.title });
    }
  };

  const confirmDelete = () => {
    setAlbums(prev => prev.filter(a => a.id !== deleteModal.id));
    message.success('Album deleted successfully');
    setDeleteModal({ visible: false, id: '', title: '' });
  };

  const cancelDelete = () => {
    setDeleteModal({ visible: false, id: '', title: '' });
  };

  return (
    <div className="my-albums">
      {/* Header */}
      <div className="my-albums__header">
        <div className="header-content">
          <h1 className="header-content__title">My Albums</h1>
        </div>
      </div>

      {/* Content */}
      <div className="my-albums__content">
        {/* Tabs */}
        <div className="tabs">
          <button 
            className="tabs__btn"
            onClick={() => navigate(ROUTES.mySongs)}
          >
            Songs
          </button>
          <button className="tabs__btn tabs__btn--active">
            Albums
          </button>
        </div>

        {/* Desktop Table */}
        <div className="table-container desktop-only">
          <table className="albums-table">
            <thead>
              <tr>
                <th>Album</th>
                <th>Tracks</th>
                <th>Release Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {albums.map(album => (
                <AlbumRow
                  key={album.id}
                  album={album}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="mobile-cards mobile-only">
          {albums.map(album => (
            <MobileAlbumCard
              key={album.id}
              album={album}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        visible={deleteModal.visible}
        title={deleteModal.title}
        type="album"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default MyAlbums;
